import React, { memo, useEffect, useRef, useState } from 'react'
import { Play } from 'lucide-react'
import { useAppStore } from '@/store/useAppStore'

// Single render component for every list/card view of a task attachment.
//
// Rules:
//   - never mount a <video> in a list/card path — even preload="metadata"
//     fetches the moov atom and was a major source of egress
//   - if the attachment has thumbUrl: render <img> + (for videos) a play overlay
//   - if it doesn't and it's an image: render <img src={url}> with lazy
//     loading and kick off a one-time lazy backfill to generate the thumb
//   - if it doesn't and it's a video: render a play-icon placeholder, no
//     network fetch at all
//   - expandable=true: caller wants click-to-mount inline (IG planner). On
//     click, swap to a real <video> element. Click again collapses back.
//   - all other callers: pass onClick to open a lightbox (TaskDetail) or
//     handle navigation elsewhere.

function PlayOverlay({ size = 28 }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      aria-hidden
    >
      <div
        className="flex items-center justify-center rounded-full"
        style={{
          width: size + 16,
          height: size + 16,
          background: 'rgba(0,0,0,0.55)',
        }}
      >
        <Play size={size} fill="#fff" stroke="#fff" />
      </div>
    </div>
  )
}

function AttachmentMediaInner({
  attachment: att,
  taskId,
  className = '',
  imgClassName = '',
  onClick,
  expandable = false,
  alt = '',
  // 'cover' (default): img fills the container, object-fit: cover. Use when
  //   the container has a fixed aspect (h-28 cards, square IG grid tiles).
  // 'natural': img renders at its natural aspect ratio, w-full h-auto. Use
  //   for layouts that adapt to the media size (DailyRecap previews).
  fit = 'cover',
  // Suppress the center play overlay when the caller already renders its own
  // video badge (e.g. IG planner's "▶ REEL" corner chip). Default true.
  showPlayOverlay = true,
}) {
  const [expanded, setExpanded] = useState(false)
  const backfillFiredRef = useRef(false)
  const backfillAttachmentThumb = useAppStore((s) => s.backfillAttachmentThumb)

  // One-shot lazy backfill on mount for legacy image attachments that lack
  // a thumb. Guarded against double-fire across StrictMode re-mounts via
  // the ref. The store-level Set + sessionStorage cap guard against multiple
  // cards on screen all firing for the same attachment.
  useEffect(() => {
    if (backfillFiredRef.current) return
    if (!att || !att.isImage) return
    if (att.thumbUrl || att.thumbBackfillFailed || att.uploading || !att.url) return
    if (!taskId || !backfillAttachmentThumb) return
    backfillFiredRef.current = true
    const fire = () => backfillAttachmentThumb(taskId, att.id)
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fire, { timeout: 2000 })
    } else {
      setTimeout(fire, 500)
    }
  }, [att, taskId, backfillAttachmentThumb])

  if (!att) return null

  const handleClick = (e) => {
    if (expandable && att.isVideo && att.url) {
      setExpanded((v) => !v)
      e.stopPropagation()
      return
    }
    if (onClick) onClick(e)
  }

  if (expanded && att.isVideo && att.url) {
    return (
      <video
        src={att.url}
        className={`${className} ${imgClassName}`.trim()}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onClick={(e) => {
          setExpanded(false)
          e.stopPropagation()
        }}
        style={{ objectFit: 'cover', display: 'block' }}
      />
    )
  }

  // Prefer thumb. For images without a thumb, fall back to the original
  // (with loading="lazy" — better than today's eager-load default).
  const displaySrc = att.thumbUrl || (att.isImage ? att.url : null)

  if (!displaySrc) {
    // Video without thumb — placeholder, zero network fetch.
    return (
      <div
        className={`${className} relative overflow-hidden`.trim()}
        onClick={onClick || handleClick}
        style={{
          background: 'var(--color-surface-raised)',
          cursor: (expandable || onClick) ? 'pointer' : 'default',
        }}
      >
        {showPlayOverlay && <PlayOverlay />}
      </div>
    )
  }

  return (
    <div
      className={`${className} relative overflow-hidden`.trim()}
      onClick={handleClick}
      style={{ cursor: (expandable || onClick) ? 'pointer' : 'default' }}
    >
      <img
        src={displaySrc}
        alt={alt || att.name || ''}
        loading="lazy"
        decoding="async"
        draggable={false}
        className={imgClassName}
        style={
          fit === 'natural'
            ? { width: '100%', height: 'auto', display: 'block', pointerEvents: 'none' }
            : { width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }
        }
      />
      {att.isVideo && showPlayOverlay && <PlayOverlay />}
    </div>
  )
}

const AttachmentMedia = memo(AttachmentMediaInner, (prev, next) => {
  const a = prev.attachment
  const b = next.attachment
  if (a === b) {
    // identical reference, props may still differ
  } else if (!a || !b) {
    return false
  } else if (
    a.url !== b.url ||
    a.thumbUrl !== b.thumbUrl ||
    a.thumbBackfillFailed !== b.thumbBackfillFailed ||
    a.uploading !== b.uploading ||
    a.isImage !== b.isImage ||
    a.isVideo !== b.isVideo
  ) {
    return false
  }
  return (
    prev.taskId === next.taskId &&
    prev.className === next.className &&
    prev.imgClassName === next.imgClassName &&
    prev.expandable === next.expandable &&
    prev.onClick === next.onClick &&
    prev.alt === next.alt &&
    prev.fit === next.fit &&
    prev.showPlayOverlay === next.showPlayOverlay
  )
})

export default AttachmentMedia
